import React from "react";
import { Palette } from "../holidayConfig";
import { range, seeded } from "./sceneUtils";

/*
 * Åska över Halloween: himlen blixtrar till då och då, och ibland slår
 * en riktig blixt ner med förgreningar. Fyra nedslag med var sin rytm
 * (15–25 s) gör att det slår ner ungefär var femte sekund utan att
 * mönstret går att förutse.
 */

export type Pt = [number, number];

/** Sicksackande blixtbana från (x0, y0) mot (x1, y1). */
export const zigzag = (rnd: () => number, x0: number, y0: number, x1: number, y1: number, steps: number, spread: number): Pt[] => {
  const pts: Pt[] = [[x0, y0]];
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    pts.push([x0 + (x1 - x0) * t + (rnd() - 0.5) * spread, y0 + (y1 - y0) * t + (rnd() - 0.5) * spread * 0.25]);
  }
  pts.push([x1, y1]);
  return pts;
};

export const toPath = (pts: Pt[]) => `M${pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L")}`;

interface BoltSpec {
  x: number;
  end: number;
  seed: number;
  duration: number;
  delay: number;
  hide?: string;
}

const BOLTS: BoltSpec[] = [
  { x: 1500, end: 600, seed: 3, duration: 15, delay: -10 },
  { x: 150, end: 610, seed: 11, duration: 18, delay: -4, hide: "hol-hide-sm" },
  { x: 1180, end: 590, seed: 23, duration: 21, delay: -12 },
  { x: 420, end: 620, seed: 37, duration: 25, delay: -2, hide: "hol-hide-sm" },
];

const Bolt: React.FC<{ b: BoltSpec; p: Palette }> = ({ b, p }) => {
  const rnd = seeded(b.seed);
  const drift = (rnd() - 0.5) * 160;
  const main = zigzag(rnd, b.x, -20, b.x + drift, b.end, 16, 70);
  const branches = range(3).map(i => {
    const start = main[3 + i * 4];
    const dir = rnd() < 0.5 ? -1 : 1;
    return zigzag(rnd, start[0], start[1], start[0] + dir * (80 + rnd() * 90), start[1] + 110 + rnd() * 90, 6, 40);
  });
  const d = toPath(main);
  const bd = branches.map(toPath).join(" ");
  const left = `${(b.x / 1600) * 100}%`;
  return (
    <div className={`hol-bolt ${b.hide ?? ""}`} style={{ animationDuration: `${b.duration}s`, animationDelay: `${b.delay}s` }}>
      {/* Himlen lyser upp kring nedslaget */}
      <div className="hol-bolt-flash" style={{ background: `radial-gradient(ellipse 45% 70% at ${left} 20%, rgba(255,245,230,0.55), ${p[300]}22 45%, transparent 75%)` }} />
      <svg viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice">
        <g fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path d={`${d} ${bd}`} stroke={p[200]} strokeWidth="14" opacity="0.25" filter="url(#toon-softer)" />
          <path d={`${d} ${bd}`} stroke={p[100]} strokeWidth="5" opacity="0.6" filter="url(#toon-soft)" />
          <path d={bd} stroke="#ffffff" strokeWidth="1.4" />
          <path d={d} stroke="#ffffff" strokeWidth="2.8" />
        </g>
        {/* Nedslagets glöd vid marken */}
        <ellipse cx={b.x + drift} cy={b.end} rx="90" ry="18" fill="#ffffff" opacity="0.35" filter="url(#toon-softer)" />
      </svg>
    </div>
  );
};

/** Vanligt kornblixtsken som lyser upp himlen. */
const SheetLightning: React.FC = () => <div className="hol-lightning" style={{ animationDuration: "8s", animationDelay: "-5s" }} />;

const Storm: React.FC<{ p: Palette }> = ({ p }) => (
  <>
    <SheetLightning />
    {BOLTS.map((b, i) => (
      <Bolt key={i} b={b} p={p} />
    ))}
  </>
);

export default Storm;
